/**
 * Utility functions for handling report configuration transformations
 */

interface AiSummary {
  summaryId: string;
  displayName: string;
  summaryType: string;
  targetSectionKey: string;
  userInstructions?: string | null;
  sourceQuestionIds: string[];
}

interface ReportConfig {
  intro?: {
    title: string;
    content: string;
  };
  aiSummaries: AiSummary[];
  customReportComponents: Array<{
    isEnabled: boolean;
    componentKey: string;
    displayOrder: number;
    titleOverride?: string;
  }>;
  includeIndividualUpdatesComponent: boolean;
}

/**
 * Maps question IDs in a report configuration from global questions to organization-specific questions
 * 
 * @param reportConfig - The original report configuration from the template
 * @param questionIdMapping - Map of original question ID to new question ID
 * @returns Transformed report configuration with updated question IDs
 */
export function mapReportConfigQuestionIds(
  reportConfig: any,
  questionIdMapping: Record<string, string>
): ReportConfig {
  console.log('[REPORT-CONFIG] Starting question ID mapping for report config');
  console.log('[REPORT-CONFIG] Question ID mapping:', questionIdMapping);
  
  // Handle null/undefined report config
  if (!reportConfig) {
    console.log('[REPORT-CONFIG] No report config provided, using default structure');
    return {
      customReportComponents: [],
      includeIndividualUpdatesComponent: true,
      aiSummaries: []
    };
  }

  // Handle missing aiSummaries
  if (!reportConfig.aiSummaries || !Array.isArray(reportConfig.aiSummaries)) {
    console.log('[REPORT-CONFIG] No aiSummaries found, returning config as-is');
    return {
      ...reportConfig,
      aiSummaries: []
    };
  }

  console.log(`[REPORT-CONFIG] Processing ${reportConfig.aiSummaries.length} AI summaries`);

  const transformedConfig = {
    ...reportConfig,
    aiSummaries: reportConfig.aiSummaries.map((summary: any, index: number) => {
      console.log(`[REPORT-CONFIG] Processing AI summary ${index + 1}: ${summary.displayName}`);
      
      if (!summary.sourceQuestionIds || !Array.isArray(summary.sourceQuestionIds)) {
        console.log(`[REPORT-CONFIG] AI summary ${index + 1} has no sourceQuestionIds, skipping`);
        return summary;
      }

      const originalIds = summary.sourceQuestionIds;
      const mappedIds = originalIds.map((questionId: string) => {
        const mappedId = questionIdMapping[questionId];
        if (mappedId) {
          console.log(`[REPORT-CONFIG] Mapped question ID: ${questionId} → ${mappedId}`);
          return mappedId;
        } else {
          console.warn(`[REPORT-CONFIG] WARNING: Question ID ${questionId} not found in mapping, keeping original ID. This should not happen.`);
          return questionId;
        }
      });

      console.log(`[REPORT-CONFIG] AI summary ${index + 1} sourceQuestionIds: [${originalIds.join(', ')}] → [${mappedIds.join(', ')}]`);

      return {
        ...summary,
        sourceQuestionIds: mappedIds
      };
    })
  };

  console.log('[REPORT-CONFIG] Successfully completed question ID mapping');
  return transformedConfig;
}

/**
 * Creates a default report configuration structure
 */
export function createDefaultReportConfig(): ReportConfig {
  return {
    customReportComponents: [],
    includeIndividualUpdatesComponent: true,
    aiSummaries: []
  };
} 